import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { MagnifyingGlassIcon, UsersIcon } from '@heroicons/react/24/outline';
import customerApi from '../../services/customerApi';
import storeApi from '../../services/storeApi';
import Notification from '../../components/notification/notification';
import {
  TWInput,
  TWSelect,
  TWDataTable,
  PageHeader,
  EmptyState,
  SegmentedToggle,
} from '../../components/ui';
import { DataTableSkeleton } from '../../data/columns';
import NoData2 from '../../assets/no-data-2.svg';
import { buildCustomerColumns } from './columns';
import {
  buildListQuery,
  buildCountsQuery,
  nextSort,
} from './customer-list-utils';
import './customers.css';

const SEARCH_DEBOUNCE_MS = 400;

const Customers = () => {
  const roles = useSelector((state) => state.authReducer.roles);
  const selectedCompany = useSelector(
    (state) => state.companyReducer.selected,
  );
  // Rol 100 = admin: tüm müşterileri görür, şirket/mağaza kapsamı yok.
  // Diğer roller = restoran: kendi şirketiyle (companyid) daima kapsanır.
  const isAdmin = roles?.includes(100);
  const companyId = isAdmin ? '' : selectedCompany?.id || '';

  const [activeTab, setActiveTab] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState({ sortBy: 'lastorder', sortOrder: 'desc' });
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    totalRecords: null,
  });
  const [customerList, setCustomerList] = useState([]);
  const [counts, setCounts] = useState(null);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(null); // null = All Stores
  const [storesLoading, setStoresLoading] = useState(!isAdmin);
  const [loading, setLoading] = useState({ listLoading: true });
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });

  // Ham arama girdisini debounce ile fetch değerine taşır.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search]);

  // Restoran tarafı: şirketin mağazaları çekilir (güvenlik kapsamı için).
  // Admin tarafında mağaza kapsamı yoktur.
  useEffect(() => {
    if (isAdmin || !companyId) {
      setStoresLoading(false);
      return undefined;
    }
    let active = true;
    setStoresLoading(true);
    storeApi
      .list(`companyid=${companyId}`)
      .then((res) => {
        if (!active) return;
        const list = res.data?.data || [];
        setStores(list);
        // Tek mağaza → otomatik o mağazaya filtreli; 0/birden fazla → All Stores.
        setStoreId(list.length === 1 ? list[0].id : null);
      })
      .catch(() => {
        if (active) setStores([]);
      })
      .finally(() => {
        if (active) setStoresLoading(false);
      });
    return () => {
      active = false;
    };
  }, [isAdmin, companyId]);

  // Sayaçlar — şirket/mağaza kapsamına göre; storeId değişince yeniden çekilir.
  useEffect(() => {
    if (storesLoading) return undefined;
    let active = true;
    customerApi
      .counts(buildCountsQuery({ companyid: companyId, storeid: storeId || '' }))
      .then((res) => {
        if (active && res.data?.data) setCounts(res.data.data);
      })
      .catch(() => {
        /* sayaç gelmezse sekmeler sayısız çizilir */
      });
    return () => {
      active = false;
    };
  }, [storesLoading, companyId, storeId]);

  // Liste — sayfalama, sekme, sıralama, arama veya mağaza değiştikçe çekilir.
  useEffect(() => {
    if (storesLoading) return undefined;
    let active = true;
    const fetchList = async () => {
      setLoading({ listLoading: true });
      setCustomerList([]);
      const query = buildListQuery({
        pageNumber: pagination.pageNumber,
        pageSize: pagination.pageSize,
        sortBy: sort.sortBy,
        sortOrder: sort.sortOrder,
        search: debouncedSearch,
        repeat: activeTab === 'repeat',
        companyid: companyId,
        storeid: storeId || '',
      });
      try {
        const res = await customerApi.list(query);
        if (!active) return;
        setCustomerList(res.data?.data || []);
        setPagination((prev) => ({
          ...prev,
          totalRecords: res.data?.totalRecords ?? 0,
        }));
      } catch (error) {
        if (active) setNotf({ display: true, type: 'error', message: error });
      } finally {
        if (active) setLoading({ listLoading: false });
      }
    };
    fetchList();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageNumber,
    pagination.pageSize,
    activeTab,
    sort.sortBy,
    sort.sortOrder,
    debouncedSearch,
    storeId,
    storesLoading,
    companyId,
  ]);

  const resetToFirstPage = () =>
    setPagination((prev) => ({ ...prev, pageNumber: 1 }));

  const onSearchChange = (e) => {
    setSearch(e.target.value);
    resetToFirstPage();
  };

  const onTabChange = (value) => {
    setActiveTab(value);
    resetToFirstPage();
  };

  const onSort = (key) => {
    setSort((prev) => nextSort(prev, key));
    resetToFirstPage();
  };

  const onStoreChange = (value) => {
    setStoreId(value === 'all' ? null : value);
    resetToFirstPage();
  };

  const tabLabel = (text, count) => (
    <span className="inline-flex items-center gap-1.5">
      {text}
      {count != null && (
        <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[0.65rem] font-bold tabular-nums text-slate-600 dark:bg-slate-700 dark:text-slate-300">
          {Number(count).toLocaleString('en-US')}
        </span>
      )}
    </span>
  );

  const columns = buildCustomerColumns({ sort, onSort });
  const hasCustomers = customerList.length > 0;
  const showStoreFilter = !isAdmin && stores.length > 1;

  const headerActions =
    pagination.totalRecords != null && !loading.listLoading ? (
      <span className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400">
        <UsersIcon className="h-4 w-4" />
        {Number(pagination.totalRecords).toLocaleString('en-US')} total
      </span>
    ) : null;

  return (
    <div className="customers-shell min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-6">
        <PageHeader
          title="Customers"
          description="View and manage all customers for your company."
          actions={headerActions}
        />

        <SegmentedToggle
          ariaLabel="Customer filter"
          value={activeTab}
          onChange={onTabChange}
          options={[
            { value: 'all', label: tabLabel('All Customers', counts?.all) },
            {
              value: 'repeat',
              label: tabLabel('Repeat Customers', counts?.repeat),
            },
          ]}
        />

        <div className="flex flex-wrap items-center gap-3">
          <div className="w-full max-w-sm">
            <TWInput
              leftIcon={MagnifyingGlassIcon}
              value={search}
              onChange={onSearchChange}
              placeholder="Search name, email or phone…"
              aria-label="Search customers"
            />
          </div>
          {showStoreFilter && (
            <div className="w-full sm:w-56">
              <TWSelect
                value={storeId || 'all'}
                onChange={onStoreChange}
                allowClear={false}
                aria-label="Filter by store"
                options={[
                  { value: 'all', label: 'All Stores' },
                  ...stores.map((s) => ({ value: s.id, label: s.name })),
                ]}
              />
            </div>
          )}
        </div>

        {loading.listLoading ? (
          <DataTableSkeleton
            rows={8}
            headers={[
              { label: 'Name', shape: 'avatar', w: 'w-40' },
              { label: 'Phone', w: 'w-32' },
              { label: 'Last Order', w: 'w-24' },
              { label: 'Life Time Spend', align: 'right', w: 'w-24' },
              { label: 'Orders', align: 'right', shape: 'pill', w: 'w-20' },
            ]}
          />
        ) : !hasCustomers ? (
          <EmptyState
            variant="dashed"
            image={NoData2}
            title="No customers found"
            description="Try adjusting your search or check back later."
          />
        ) : (
          <div className="reveal">
            <TWDataTable
              columns={columns}
              data={customerList}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                current: pagination.pageNumber,
                pageSize: pagination.pageSize,
                total: pagination.totalRecords,
                showTotal: (total, range) =>
                  `Showing ${range[0]} to ${range[1]} of ${total} results`,
                onChange: (page, pageSize) =>
                  setPagination((prev) => ({
                    ...prev,
                    pageNumber: page,
                    pageSize,
                  })),
              }}
            />
          </div>
        )}
      </div>

      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </div>
  );
};

export default Customers;
